import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar, Clock, Users, Shield, AlertTriangle, FileText,
  Heart, Wrench, RefreshCw, Building2, ChevronRight, UserX,
  ClipboardList, Target
} from 'lucide-react';

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default', link }) {
  const variants = {
    default: 'bg-card hover:border-primary/30',
    primary: 'bg-primary/5 border-primary/20 hover:border-primary/40',
    success: 'bg-green-500/5 border-green-500/20 hover:border-green-500/40',
    warning: 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40',
    danger: 'bg-red-500/5 border-red-500/20 hover:border-red-500/40',
  };

  const content = (
    <Card className={`${variants[variant]} transition-colors ${link ? 'cursor-pointer' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {Icon && (
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                variant === 'danger' ? 'bg-red-500/10' :
                variant === 'warning' ? 'bg-amber-500/10' :
                variant === 'success' ? 'bg-green-500/10' :
                'bg-primary/10'
              }`}>
                <Icon className={`h-5 w-5 ${
                  variant === 'danger' ? 'text-red-500' :
                  variant === 'warning' ? 'text-amber-500' :
                  variant === 'success' ? 'text-green-500' :
                  'text-primary'
                }`} />
              </div>
            )}
            {link && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

function DetailCard({ title, items }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center py-1.5">
            <div className="flex items-center gap-2">
              {item.icon && <item.icon className={`h-4 w-4 ${item.iconColor || 'text-muted-foreground'}`} />}
              <span className="text-sm">{item.label}</span>
            </div>
            <span className={`font-medium ${item.valueColor || ''}`}>{item.value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function OperationsDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, branchRes] = await Promise.all([
        api.get('/kpi/operations', { params }),
        api.get('/org/branches'),
      ]);
      setKpis(kpiRes.data.data);
      setBranches(branchRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]);

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const details = kpis?.details || {};

  // Calculate alert totals
  const totalAlerts = (kpis?.expiringCompliance || 0) + 
                      (kpis?.participantsWithAlerts || 0) + 
                      (kpis?.carePlansDueForReview || 0) + 
                      (kpis?.riskAssessmentsDue || 0);

  return (
    <div className="space-y-6" data-testid="operations-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Operations Dashboard</h1>
          <p className="text-muted-foreground">Daily operations and compliance overview</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px]" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {totalAlerts > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div className="flex-1">
                <p className="font-medium">Items Requiring Attention</p>
                <p className="text-sm text-muted-foreground">
                  {kpis?.expiringCompliance > 0 && <span>{kpis.expiringCompliance} expiring compliance</span>}
                  {kpis?.expiringCompliance > 0 && kpis?.carePlansDueForReview > 0 && ' · '}
                  {kpis?.carePlansDueForReview > 0 && <span>{kpis.carePlansDueForReview} care plans due</span>}
                  {(kpis?.expiringCompliance > 0 || kpis?.carePlansDueForReview > 0) && kpis?.riskAssessmentsDue > 0 && ' · '}
                  {kpis?.riskAssessmentsDue > 0 && <span>{kpis.riskAssessmentsDue} risk reviews due</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary KPIs - Row 1: Roster & Staffing */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Roster & Staffing</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Today's Shifts"
            value={kpis?.todayShifts || 0}
            subtitle={`${details.roster?.todayFilled || 0} filled, ${details.roster?.todayUnfilled || 0} unfilled`}
            icon={Calendar}
            variant="primary"
            link="/roster/calendar"
          />
          <KpiCard
            title="Unassigned Shifts"
            value={kpis?.unassignedShifts || 0}
            subtitle={`${details.roster?.unassignedToday || 0} for today`}
            icon={Clock}
            variant={kpis?.unassignedShifts > 10 ? 'danger' : kpis?.unassignedShifts > 5 ? 'warning' : 'default'}
            link="/roster/calendar"
          />
          <KpiCard
            title="Staff on Leave Today"
            value={kpis?.staffOnLeaveToday || 0}
            subtitle={Object.keys(details.leave?.byType || {}).length > 0 
              ? Object.entries(details.leave.byType).map(([t, c]) => `${c} ${t}`).join(', ')
              : 'No leave requests'}
            icon={UserX}
            link="/hr/leave"
          />
        </div>
      </div>

      {/* Primary KPIs - Row 2: Compliance & Clinical */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Compliance & Reviews</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiCard
            title="Expiring Compliance"
            value={kpis?.expiringCompliance || 0}
            subtitle={`${details.compliance?.expiring7Days || 0} within 7 days · ${details.compliance?.expired || 0} expired`}
            icon={Shield}
            variant={kpis?.expiringCompliance > 10 ? 'danger' : kpis?.expiringCompliance > 5 ? 'warning' : 'success'}
            link="/compliance/staff"
          />
          <KpiCard
            title="Care Plans Due for Review"
            value={kpis?.carePlansDueForReview || 0}
            subtitle={`${details.carePlans?.reviewUpcoming || 0} upcoming in 14 days`}
            icon={Heart}
            variant={kpis?.carePlansDueForReview > 0 ? 'warning' : 'success'}
          />
          <KpiCard
            title="Risk Assessments Due"
            value={kpis?.riskAssessmentsDue || 0}
            subtitle={`${details.riskAssessments?.reviewUpcoming || 0} upcoming in 14 days`}
            icon={Target}
            variant={kpis?.riskAssessmentsDue > 0 ? 'warning' : 'success'}
          />
        </div>
      </div>

      {/* Primary KPIs - Row 3: Participants & Documentation */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Participants & Documentation</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Participants with Alerts"
            value={kpis?.participantsWithAlerts || 0}
            subtitle={`${details.participants?.withIncidents || 0} with incidents · ${details.participants?.withHighRisk || 0} high risk`}
            icon={AlertTriangle}
            variant={kpis?.participantsWithAlerts > 0 ? 'danger' : 'success'}
            link="/compliance/incidents"
          />
          <KpiCard
            title="Overdue Progress Notes"
            value={kpis?.overdueProgressNotes || 0}
            subtitle="Participants without notes in 7 days"
            icon={FileText}
            variant={kpis?.overdueProgressNotes > 5 ? 'warning' : 'default'}
          />
          <KpiCard
            title="Open Maintenance Issues"
            value={kpis?.openMaintenanceIssues || 0}
            subtitle={`${details.maintenance?.urgent || 0} urgent/high priority`}
            icon={Wrench}
            variant={details.maintenance?.urgent > 0 ? 'danger' : kpis?.openMaintenanceIssues > 5 ? 'warning' : 'default'}
            link="/maintenance"
          />
        </div>
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Roster Details */}
        <DetailCard
          title="Roster Summary"
          items={[
            { label: "Today's Total Shifts", value: kpis?.todayShifts || 0, icon: Calendar },
            { label: 'Filled Today', value: details.roster?.todayFilled || 0, icon: Users, iconColor: 'text-green-500' },
            { label: 'Unfilled Today', value: details.roster?.todayUnfilled || 0, icon: Clock, iconColor: kpis?.todayShifts - (details.roster?.todayFilled || 0) > 0 ? 'text-amber-500' : '' },
            { label: 'Total Unassigned (Future)', value: kpis?.unassignedShifts || 0, valueColor: kpis?.unassignedShifts > 10 ? 'text-red-600' : '' },
            { label: 'Staff on Leave', value: kpis?.staffOnLeaveToday || 0, icon: UserX },
          ]}
        />

        {/* Compliance Details */}
        <DetailCard
          title="Compliance Status"
          items={[
            { label: 'Expiring in 7 Days', value: details.compliance?.expiring7Days || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: details.compliance?.expiring7Days > 0 ? 'text-red-600 font-bold' : '' },
            { label: 'Expiring in 30 Days', value: kpis?.expiringCompliance || 0, icon: Shield, iconColor: 'text-amber-500' },
            { label: 'Already Expired', value: details.compliance?.expired || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: details.compliance?.expired > 0 ? 'text-red-600 font-bold' : '' },
          ]}
        />

        {/* Clinical Reviews */}
        <DetailCard
          title="Clinical Reviews"
          items={[
            { label: 'Care Plans Overdue', value: kpis?.carePlansDueForReview || 0, icon: Heart, iconColor: 'text-amber-500', valueColor: kpis?.carePlansDueForReview > 0 ? 'text-amber-600' : '' },
            { label: 'Care Plans Upcoming (14d)', value: details.carePlans?.reviewUpcoming || 0 },
            { label: 'Risk Assessments Overdue', value: kpis?.riskAssessmentsDue || 0, icon: Target, iconColor: 'text-amber-500', valueColor: kpis?.riskAssessmentsDue > 0 ? 'text-amber-600' : '' },
            { label: 'Risk Assessments Upcoming (14d)', value: details.riskAssessments?.reviewUpcoming || 0 },
            { label: 'Overdue Progress Notes', value: kpis?.overdueProgressNotes || 0, icon: FileText },
          ]}
        />

        {/* Maintenance Details */}
        <DetailCard
          title="Maintenance Status"
          items={[
            { label: 'Total Open', value: kpis?.openMaintenanceIssues || 0, icon: Wrench },
            { label: 'Urgent', value: details.maintenance?.byPriority?.URGENT || 0, iconColor: 'text-red-500', valueColor: (details.maintenance?.byPriority?.URGENT || 0) > 0 ? 'text-red-600 font-bold' : '' },
            { label: 'High Priority', value: details.maintenance?.byPriority?.HIGH || 0, iconColor: 'text-amber-500', valueColor: (details.maintenance?.byPriority?.HIGH || 0) > 0 ? 'text-amber-600' : '' },
            { label: 'Medium Priority', value: details.maintenance?.byPriority?.MEDIUM || 0 },
            { label: 'Low Priority', value: details.maintenance?.byPriority?.LOW || 0 },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" asChild>
          <Link to="/roster/calendar">View Roster</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/compliance/staff">Staff Compliance</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/hr/leave">Leave Approvals</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/maintenance">Maintenance</Link>
        </Button>
      </div>

      {/* Data Sources & Period */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Data Sources:</span> roster, leave, compliance, progress notes, care plans, risk assessments, maintenance
            </div>
            <div>
              Generated: {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
